import type { Joint, Limitation } from '@cadencia/contracts';
import type { Exercise } from '../catalog/exercises.js';
import type { MovementPattern } from '../catalog/muscles.js';

/**
 * Lesiones y limitaciones declaradas en el test inicial (docs/TEST_INICIAL.md
 * §2.2).
 *
 * LO QUE ESTO NO ES
 *
 * No es un diagnóstico ni una pauta de readaptación de una lesión. La
 * aplicación no valora lesiones —lo dice `safety/red-flags.ts` y vale igual
 * aquí—: solo deja de PROPONER lo que el atleta ha dicho que le molesta. Si
 * algo duele de verdad, la respuesta es un profesional, no un filtro.
 *
 * LA RELACIÓN PATRÓN → ARTICULACIÓN ES UNA CONVENCIÓN DECLARADA
 *
 * Qué articulación carga cada patrón de movimiento, escrito una vez. Es
 * deliberadamente amplio —un press horizontal carga hombro, codo y muñeca—
 * porque aquí equivocarse por exceso cuesta un ejercicio, y por defecto, la
 * articulación que el atleta pidió cuidar. La carga axial alta suma además la
 * zona lumbar y el cuello: una barra a la espalda o un peso muerto pesado los
 * cargan por mucho que el patrón no lo diga.
 */
export const ARTICULACIONES_POR_PATRON: Record<MovementPattern, readonly Joint[]> = {
  horizontal_push: ['shoulder', 'elbow', 'wrist'],
  vertical_push: ['shoulder', 'elbow', 'wrist'],
  horizontal_pull: ['shoulder', 'elbow'],
  vertical_pull: ['shoulder', 'elbow'],
  squat: ['knee', 'hip', 'ankle'],
  hinge: ['lower_back', 'hip'],
  lunge: ['knee', 'hip', 'ankle'],
  isolation_elbow_flex: ['elbow', 'wrist'],
  isolation_elbow_ext: ['elbow'],
  isolation_knee_flex: ['knee'],
  isolation_knee_ext: ['knee'],
  isolation_shoulder_abd: ['shoulder'],
  isolation_chest_fly: ['shoulder'],
  isolation_calf: ['ankle'],
  isolation_hip: ['hip'],
  core: ['lower_back'],
};

/** Desde qué carga axial un ejercicio carga también la zona lumbar y el cuello. */
export const CARGA_AXIAL_QUE_CARGA_LA_COLUMNA = 4;

/**
 * Hasta dónde se permite un ejercicio en una articulación en `caution`: solo
 * lo sencillo y ligero. Es la diferencia con `avoid`, que lo quita todo.
 */
export const PRECAUCION = {
  maxTechnicalDemand: 2,
  maxAxialLoad: 2,
} as const;

export const ETIQUETA_ARTICULACION: Record<Joint, string> = {
  neck: 'Cuello',
  shoulder: 'Hombro',
  elbow: 'Codo',
  wrist: 'Muñeca',
  lower_back: 'Zona lumbar',
  hip: 'Cadera',
  knee: 'Rodilla',
  ankle: 'Tobillo',
};

/** Las articulaciones que carga un ejercicio. */
export function jointsLoadedBy(exercise: Exercise): Joint[] {
  const articulaciones = new Set<Joint>(ARTICULACIONES_POR_PATRON[exercise.pattern]);
  if (exercise.axialLoad >= CARGA_AXIAL_QUE_CARGA_LA_COLUMNA) {
    articulaciones.add('lower_back');
    articulaciones.add('neck');
  }
  return [...articulaciones];
}

export interface VeredictoDeLimitacion {
  allowed: boolean;
  /** Por qué no, en palabras. Vacío si se permite. */
  reasons: string[];
}

/** ¿Se puede proponer este ejercicio a este atleta? */
export function exerciseAllowed(
  exercise: Exercise,
  limitations: readonly Limitation[],
): VeredictoDeLimitacion {
  if (limitations.length === 0) return { allowed: true, reasons: [] };

  const cargadas = new Set(jointsLoadedBy(exercise));
  const reasons: string[] = [];

  for (const limitacion of limitations) {
    if (!cargadas.has(limitacion.joint)) continue;
    const zona = ETIQUETA_ARTICULACION[limitacion.joint].toLowerCase();
    if (limitacion.level === 'avoid') {
      reasons.push(`carga ${zona}, que has pedido no cargar`);
      continue;
    }
    const sencillo =
      exercise.technicalDemand <= PRECAUCION.maxTechnicalDemand &&
      exercise.axialLoad <= PRECAUCION.maxAxialLoad;
    if (!sencillo) reasons.push(`carga ${zona} con demasiada técnica o carga para ir con cuidado`);
  }

  return { allowed: reasons.length === 0, reasons };
}

/** El catálogo sin lo que las limitaciones vetan. */
export function filterByLimitations<T extends Exercise>(
  exercises: readonly T[],
  limitations: readonly Limitation[],
): T[] {
  return exercises.filter((exercise) => exerciseAllowed(exercise, limitations).allowed);
}
